import './index.scss';

import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import axios from '../../admin-axios';
import { ReactComponent as CashIcon } from '../../assets/Images/cash_icon.svg';
import { ReactComponent as CylinderIcon } from '../../assets/Images/cylinder_icon.svg';
import MyResponsiveBar from '../../components/BarChart';
import useActions from '../../hooks/useActions';
import { convertTons, detectError, nFormatter } from '../../utils';

const Dashboard = () => {
  const history = useHistory();
  const {
    loading,
    currency,
    stationName,
    totalGasToday,
    thisMonth,
    totalGasMonth,
    totalSalesMonth,
    totalSalesToday,
    salesGraph,
    totalUnapproved,
    recentApproved
  } = useSelector((state) => state.dashboard);

  const { setHeader } = useActions();

  const {
    user: { stationId }
  } = useSelector((state) => state.auth);

  const { getDashboardStats } = useActions();

  const [tranxID, setTranxID] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    getDashboardStats(stationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  useEffect(() => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const findUnapprovedTransaction = async () => {
    try {
      if (!tranxID) {
        toast.error('Please enter a unapproved transaction ID');
        return;
      }
      setSearching(true);
      const response = await axios.get(`/transaction/search/unapproved/${tranxID}`);
      const { id } = response.data.data;
      setSearching(false);
      setHeader('Unapproved Transactions');
      history.push(`unapproved/${id}`);
    } catch (e) {
      const message = detectError(e);
      toast.error(`${message}. Please ensure it is an unapproved transaction ID.`);
      setSearching(false);
    }
  };

  return (
    <div className="dashboard">
      <div className="container">
        <h2 className="mb-5">{stationName}</h2>
        <div className="row g-5 mb-5">
          <div className="col-xl-6">
            <div className="row g-3">
              <div className="col-6">
                <div className="dashboard_gas_sold dashboard_box w-100">
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center w-100 h-100">
                      <Spinner className="spinner" animation="border" variant="dark" />
                    </div>
                  ) : (
                    <>
                      <CylinderIcon />
                      <p className="dashboard_box_main_text">{convertTons(totalGasToday)}</p>
                      <p className="dashboard_box_sub_text">Total gas sold today</p>
                    </>
                  )}
                </div>
              </div>
              <div className="col-6">
                <div className="dashboard_total_gas_sold dashboard_box w-100">
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center w-100 h-100">
                      <Spinner className="spinner" animation="border" variant="light" />
                    </div>
                  ) : (
                    <>
                      <CylinderIcon />
                      <p className="dashboard_box_main_text">{convertTons(totalGasMonth)}</p>
                      <p className="dashboard_box_sub_text">Total gas sold ({thisMonth})</p>
                    </>
                  )}
                </div>
              </div>
              <div className="col-6">
                <div className="dashboard_sales dashboard_box w-100">
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center w-100 h-100">
                      <Spinner className="spinner" animation="border" variant="dark" />
                    </div>
                  ) : (
                    <>
                      <CashIcon />
                      <p className="dashboard_box_main_text">
                        {currency}
                        {nFormatter(totalSalesToday, 3)}
                      </p>
                      <p className="dashboard_box_sub_text">Total sales made today</p>
                    </>
                  )}
                </div>
              </div>
              <div className="col-6">
                <div className="dashboard_total_sales dashboard_box w-100">
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center w-100 h-100">
                      <Spinner className="spinner" animation="border" variant="light" />
                    </div>
                  ) : (
                    <>
                      <CashIcon />
                      <p className="dashboard_box_main_text">
                        {currency}
                        {nFormatter(totalSalesMonth, 3)}
                      </p>
                      <p className="dashboard_box_sub_text">Total sales made ({thisMonth})</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-6">
            <div className="d-flex justify-content-center align-items-center dashboard_graph ps-2">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center w-100 h-100">
                  <Spinner className="spinner" animation="border" variant="dark" />
                </div>
              ) : (
                <MyResponsiveBar data={salesGraph} currency={currency} />
              )}
            </div>
          </div>
        </div>
        <h4 className="transaction mb-3">Transactions</h4>
        <div className="row g-3 mb-5">
          <div className="col-sm-6 col-lg-8">
            <div className="input-group dashboard_search">
              <input
                type="text"
                className="form-control p-2 dashboard_search_input"
                placeholder="Search for Unapproved Transactions by Transaction ID"
                aria-label="transactions search"
                aria-describedby="button-addon2"
                value={tranxID}
                onChange={(e) => setTranxID(e.target.value)}
              />
              {searching ? (
                <div className="dashboard_search_spinner d-flex justify-content-center align-items-center">
                  <Spinner className="spinner" animation="border" variant="dark" />
                </div>
              ) : (
                <button
                  className="btn dashboard_search_button"
                  onClick={findUnapprovedTransaction}
                  type="button"
                  id="button-addon2">
                  View
                </button>
              )}
            </div>
          </div>
          <div className=" col-sm-6 col-lg-4">
            <Link
              to="/unapproved"
              onClick={() => setHeader('Unapproved Transactions')}
              className="btn dashboard_search_link">
              Unapproved Transactions{' '}
              <span className="badge bg-danger ms-2">{totalUnapproved}</span>
            </Link>
          </div>
        </div>
        <div>
          <div className="d-flex justify-content-between mb-3">
            <h4 className="dashboard_txs_header">Recent Approved Transactions</h4>
            <Link
              to="/approved"
              onClick={() => setHeader('Approved Transactions')}
              className="btn dashboard_view_more fw-bold px-4 py-2"
              type="button">
              View More
            </Link>
          </div>
          <div className="dashboard_table_wrapper">
            <table className="table table-striped dashboard_table">
              <thead>
                <tr>
                  <th scope="col">Transaction ID</th>
                  <th scope="col">Customer Name</th>
                  <th scope="col">Customer Type</th>
                  <th scope="col">Phone No.</th>
                  <th scope="col">Gas Quantity Purchased</th>
                  <th scope="col">Price/Kg</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {/* eslint-disable-next-line no-nested-ternary */}
                {loading ? (
                  <tr className="py-3">
                    <td colSpan={9}>
                      <div className="d-flex justify-content-center w-100">
                        <Spinner className="spinner" animation="border" variant="dark" />
                      </div>
                    </td>
                  </tr>
                ) : recentApproved && recentApproved.length > 0 ? (
                  recentApproved.map((tx) => (
                    <tr key={tx.transaction_id}>
                      <td>{tx.transaction_id}</td>
                      <td>
                        {tx.firstname} {tx.lastname}
                      </td>
                      <td>{tx.customer_type}</td>
                      <td>{tx.phone}</td>
                      <td>{tx.gas_quantity}</td>
                      <td>{tx.gas_price}</td>
                      <td>{tx.amount}</td>
                      <td
                        style={{
                          color: tx.status === 'Cleared' ? 'blue' : 'red',
                          fontWeight: 'bold'
                        }}>
                        {tx.status}
                      </td>
                      <td>
                        <Link
                          to={`approved/${tx.transaction_id}`}
                          className="btn dashboard_table_button">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="w-100 text-center">
                      No Recent Approved Transactions
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
